import { useState, useRef } from "react";
import { ItemSchemaField } from "@/api/client";

interface DynamicItemFormProps {
  fields: ItemSchemaField[];
  initialData?: Record<string, unknown>;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export default function DynamicItemForm({
  fields,
  initialData = {},
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = "Save",
}: DynamicItemFormProps) {
  // Use a ref to track if we've initialized, to prevent resetting on every render
  const initializedRef = useRef(false);

  const [formData, setFormData] = useState<Record<string, unknown>>(() => {
    // Initialize with default values on first render
    const data = { ...initialData };
    fields.forEach((field) => {
      if (data[field.name] === undefined) {
        data[field.name] = field.default_value ?? "";
      }
    });
    initializedRef.current = true;
    return data;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      const value = formData[field.name];
      if (field.required && !value && value !== 0 && value !== false) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (fieldName: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    // Clear error when user types
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const renderField = (field: ItemSchemaField) => {
    const value = formData[field.name] ?? "";
    const error = errors[field.name];
    const inputClassName = `input w-full ${error ? "border-red-500" : ""}`;

    switch (field.type) {
      case "textarea":
        return (
          <textarea
            id={field.name}
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={inputClassName}
            placeholder={field.placeholder}
            rows={3}
          />
        );

      case "select":
        return (
          <select
            id={field.name}
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={inputClassName}
          >
            <option value="">Select {field.label}...</option>
            {(field.options || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "url":
        return (
          <input
            type="url"
            id={field.name}
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={inputClassName}
            placeholder={field.placeholder || "https://..."}
          />
        );

      case "number":
        return (
          <input
            type="number"
            id={field.name}
            value={value as number}
            onChange={(e) =>
              handleChange(
                field.name,
                e.target.value ? Number(e.target.value) : "",
              )
            }
            className={inputClassName}
            placeholder={field.placeholder}
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={field.name}
              checked={Boolean(value)}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
            />
            <label htmlFor={field.name} className="ml-2 text-sm text-gray-600">
              {field.placeholder || field.label}
            </label>
          </div>
        );

      case "text":
      default:
        return (
          <input
            type="text"
            id={field.name}
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={inputClassName}
            placeholder={field.placeholder}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((field) => (
          <div
            key={field.name}
            className={field.type === "textarea" ? "md:col-span-2" : ""}
          >
            {field.type !== "checkbox" && (
              <label htmlFor={field.name} className="label">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {renderField(field)}
            {errors[field.name] && (
              <p className="mt-1 text-xs text-red-500">{errors[field.name]}</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  );
}

// Bulk add component for pasting multiple items
interface BulkAddFormProps {
  fields: ItemSchemaField[];
  onSubmit: (items: Record<string, unknown>[]) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function BulkAddForm({
  fields,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: BulkAddFormProps) {
  const [input, setInput] = useState("");
  const [format, setFormat] = useState<"csv" | "json">("csv");
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Record<string, unknown>[]>([]);

  // Parse a CSV line, handling quoted values that may contain commas
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        values.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };

  const parseInput = () => {
    setParseError(null);
    setPreview([]);

    if (!input.trim()) {
      return;
    }

    try {
      if (format === "json") {
        const parsed = JSON.parse(input);
        const items = Array.isArray(parsed) ? parsed : [parsed];
        setPreview(items);
      } else {
        // CSV parsing - use field names from schema as headers
        // Each line is data, no header row required
        const lines = input.trim().split("\n");
        if (lines.length === 0) {
          setParseError("No data provided");
          return;
        }

        const fieldNames = fields.map((f) => f.name);
        const items: Record<string, unknown>[] = [];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // Skip empty lines

          const values = parseCSVLine(line);

          if (values.length !== fieldNames.length) {
            setParseError(
              `Line ${i + 1}: Expected ${fieldNames.length} values (${fieldNames.join(", ")}), got ${values.length}`,
            );
            return;
          }

          const item: Record<string, unknown> = {};
          fieldNames.forEach((name, index) => {
            item[name] = values[index] || "";
          });
          items.push(item);
        }

        if (items.length === 0) {
          setParseError("No valid data rows found");
          return;
        }

        setPreview(items);
      }
    } catch {
      setParseError(
        format === "json" ? "Invalid JSON format" : "Failed to parse CSV",
      );
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (preview.length > 0) {
      onSubmit(preview);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <div className="flex items-center gap-4 mb-2">
          <label className="label mb-0">Format:</label>
          <label className="flex items-center">
            <input
              type="radio"
              value="csv"
              checked={format === "csv"}
              onChange={() => setFormat("csv")}
              className="mr-2"
            />
            CSV
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="json"
              checked={format === "json"}
              onChange={() => setFormat("json")}
              className="mr-2"
            />
            JSON
          </label>
        </div>

        <div className="text-xs text-gray-500 mb-2">
          {format === "csv" ? (
            <span>
              Columns (in order): {fields.map((f) => f.name).join(", ")}
              <br />
              <span className="text-gray-400">
                No header row needed. Use quotes for values containing commas.
              </span>
            </span>
          ) : (
            <span>
              Expected format:{" "}
              {"[{" +
                fields.map((f) => `"${f.name}": "..."`).join(", ") +
                "}, ...]"}
            </span>
          )}
        </div>

        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            format === "csv"
              ? `value1,value2,value3\nvalue1,value2,value3`
              : `[{"${fields[0]?.name || "field"}": "value"}]`
          }
          className="input w-full font-mono text-sm"
          rows={8}
        />

        <button
          type="button"
          onClick={parseInput}
          className="btn btn-secondary mt-2"
        >
          Preview
        </button>
      </div>

      {parseError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
          {parseError}
        </div>
      )}

      {preview.length > 0 && (
        <div className="border rounded-md overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b">
            Preview ({preview.length} items)
          </div>
          <div className="max-h-48 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {fields.map((field) => (
                    <th
                      key={field.name}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      {field.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {preview.map((item, index) => (
                  <tr key={index}>
                    {fields.map((field) => (
                      <td
                        key={field.name}
                        className="px-3 py-2 whitespace-nowrap"
                      >
                        {String(item[field.name] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || preview.length === 0}
        >
          {isSubmitting ? "Adding..." : `Add ${preview.length} Items`}
        </button>
      </div>
    </form>
  );
}
